import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const DemoContext = createContext(null);
const PAUSE_MS = 7000;

export const DemoProvider = ({ children }) => {
  const [selected, setSelected] = useState("shashin");
  const [mode, setMode] = useState("peek");
  const [zoom, setZoom] = useState(false);
  const [highlight, setHighlight] = useState(null);
  const [sent, setSent] = useState({});
  const [preview, setPreview] = useState(null);
  const [pausedUntil, setPausedUntil] = useState(0);
  const pause = useRef({ until: 0, step: null, at: 0 });
  const activeStep = useRef(null);

  const markInteraction = useCallback((step) => {
    const now = Date.now();
    pause.current = { until: now + PAUSE_MS, step: step === undefined ? activeStep.current : step, at: now };
    setPausedUntil(now + PAUSE_MS);
  }, []);

  const applyState = useCallback((s) => {
    if (s.selected) setSelected(s.selected);
    if (s.mode) setMode(s.mode);
    setZoom(!!s.zoom);
    setHighlight(s.highlight || null);
  }, []);

  const shouldStepApply = useCallback((step) => {
    const p = pause.current;
    const now = Date.now();
    if (now >= p.until) return true;
    return step !== p.step && now - p.at > 2500;
  }, []);

  const sendMessage = useCallback((id, text) => {
    setSent((prev) => ({
      ...prev,
      [id]: [...(prev[id] || []), { id: `s-${Date.now()}`, type: "text", from: "me", text, sample: true }],
    }));
  }, []);

  const value = useMemo(
    () => ({
      selected, setSelected, mode, setMode, zoom, setZoom, highlight, setHighlight,
      sent, sendMessage, preview, setPreview, pausedUntil, markInteraction, applyState,
      shouldStepApply, activeStep,
    }),
    [selected, mode, zoom, highlight, sent, sendMessage, preview, pausedUntil, markInteraction, applyState, shouldStepApply]
  );

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
};

export const useDemo = () => useContext(DemoContext);
